/**
 * Everything derived: day totals, the target in force on each day, trend
 * weight, the expenditure series, the current estimate. Pure — the screens
 * feed it rows from Dexie via db/queries.useModel().
 */

import { addDays, dayRange } from './days.ts'
import { expenditureSeries, isValidDay, type ExpenditurePoint, type IntakeDay } from './expenditure.ts'
import type { DaySummary } from './insights.ts'
import { add, type Nutrients } from './nutrients.ts'
import { bmrFor, priorExpenditure, type ProfileLike } from './targets.ts'
import { dailyWeights, trendOn, trendSeries, weeklyRate, type TrendPoint } from './trend.ts'
import { effectiveTarget, trainingShift, type EffectiveTarget, type Shift } from './shift.ts'

export type FoodLike = { day: string; kcal: number; protein_g: number; carbs_g: number; fat_g: number;
  fibre_g?: number | null; nutrients?: Nutrients | null; deleted_at?: string | null }
export type TargetLike = { effective_from: string; kcal: number; protein_g: number; carbs_g: number;
  fat_g: number; fibre_g?: number | null; deleted_at?: string | null; updated_at?: string }
export type StatusLike = { day: string; status: 'auto' | 'complete' | 'incomplete'; deleted_at?: string | null }

export type DayTotals = { kcal: number; protein_g: number; carbs_g: number; fat_g: number;
  fibre_g: number | null; nutrients: Nutrients; entries: number }

export function totalsByDay(foods: FoodLike[]): Map<string, DayTotals> {
  const m = new Map<string, DayTotals>()
  for (const f of foods) {
    if (f.deleted_at) continue
    const t = m.get(f.day) ?? { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fibre_g: null, nutrients: {}, entries: 0 }
    t.kcal += f.kcal || 0
    t.protein_g += f.protein_g || 0
    t.carbs_g += f.carbs_g || 0
    t.fat_g += f.fat_g || 0
    if (f.fibre_g !== null && f.fibre_g !== undefined) t.fibre_g = (t.fibre_g ?? 0) + f.fibre_g
    add(t.nutrients, f.nutrients)
    t.entries++
    m.set(f.day, t)
  }
  return m
}

/** Live targets sorted oldest-first; the one for `day` is the last with effective_from <= day. */
export function liveTargets<T extends TargetLike>(targets: T[]): T[] {
  return targets.filter((t) => !t.deleted_at)
    .sort((a, b) => (a.effective_from === b.effective_from
      ? (a.updated_at ?? '').localeCompare(b.updated_at ?? '')
      : a.effective_from.localeCompare(b.effective_from)))
}

export function targetFor<T extends TargetLike>(sorted: T[], day: string): T | null {
  let hit: T | null = null
  for (const t of sorted) {
    if (t.effective_from > day) break
    hit = t
  }
  // Days before the first target are judged against it rather than nothing.
  return hit ?? sorted[0] ?? null
}

export type Model = {
  today: string
  totals: Map<string, DayTotals>
  summaries: Map<string, DaySummary>
  targets: TargetLike[]
  trend: TrendPoint[]
  trendNow: number | null
  rate21: number | null
  rate14: number | null
  expenditure: ExpenditurePoint[]
  eNow: number | null
  confidence: number
  bmr: number | null
  prior: number | null
  weighDays: Set<string>
  lastWeighIn: string | null
  burns: Map<string, number>
  shift: Shift
  /** The day's target after the training-day shift, or null before any target. */
  targetOn: (day: string) => EffectiveTarget | null
}

export function buildModel(opts: {
  today: string
  foods: FoodLike[]
  statuses: StatusLike[]
  weighins: { day: string; kg: number }[]
  targets: TargetLike[]
  profile: (ProfileLike & { program_start?: string | null; training_shift?: boolean | null }) | null
  workouts?: { day: string; kcal: number; deleted_at?: string | null }[]
}): Model {
  const { today, foods, statuses, weighins, profile } = opts
  const totals = totalsByDay(foods)
  const targets = liveTargets(opts.targets)
  const burns = new Map<string, number>()
  for (const w of opts.workouts ?? []) {
    if (!w.deleted_at) burns.set(w.day, (burns.get(w.day) ?? 0) + (w.kcal || 0))
  }
  const shift = trainingShift(burns, today, profile?.training_shift !== false)
  const targetOn = (day: string) => {
    const t = targetFor(targets, day)
    return t ? effectiveTarget(t, shift, day) : null
  }
  const status = new Map(statuses.filter((s) => !s.deleted_at).map((s) => [s.day, s.status]))

  const summaries = new Map<string, DaySummary>()
  const intake = new Map<string, IntakeDay>()
  const days = new Set<string>([...totals.keys(), ...status.keys()])
  for (const d of days) {
    const t = totals.get(d)
    const tgt = targetOn(d)
    const st = status.get(d)
    // Today only counts once it's marked complete: a half-eaten day is not a
    // low-intake day.
    const valid = d === today ? st === 'complete'
      : isValidDay(t?.kcal ?? 0, t?.entries ?? 0, st, tgt?.kcal ?? 2000)
    summaries.set(d, {
      day: d, kcal: t?.kcal ?? 0, protein_g: t?.protein_g ?? 0, fibre_g: t?.fibre_g ?? null,
      sodium_mg: t?.nutrients.sodium_mg ?? null, entries: t?.entries ?? 0, valid,
      target: tgt ? { kcal: tgt.kcal, protein_g: tgt.protein_g, fibre_g: tgt.fibre_g ?? null } : null,
    })
    intake.set(d, { kcal: t?.kcal ?? 0, valid })
  }

  const daily = dailyWeights(weighins)
  const trend = trendSeries(daily)
  const trendNow = trendOn(trend, today)
  const weighDays = new Set(daily.map((d) => d.day))
  const lastWeighIn = daily.length ? daily[daily.length - 1].day : null

  let expenditure: ExpenditurePoint[] = []
  let bmr: number | null = null
  let prior: number | null = null
  const firstFood = [...totals.keys()].sort()[0]
  const start = profile?.program_start ?? firstFood
  if (profile && profile.sex && trendNow !== null && start) {
    const kgAtStart = trendOn(trend, start) ?? trend[0]?.trend ?? trendNow
    bmr = bmrFor(profile, trendNow, today)
    prior = priorExpenditure(profile, kgAtStart, start)
    const from = start < today ? start : addDays(today, -1)
    expenditure = expenditureSeries({ from, to: today, prior, bmr, intake, weights: trend })
  }
  const last = expenditure[expenditure.length - 1]

  return {
    today, totals, summaries, targets, trend, trendNow,
    rate21: weeklyRate(trend, today, 21), rate14: weeklyRate(trend, today, 14),
    expenditure, eNow: last ? last.E : prior, confidence: last ? last.c : 0,
    bmr, prior, weighDays, lastWeighIn, burns, shift, targetOn,
  }
}

/** Expenditure on a past day (for "vs 4 weeks ago"). */
export function expenditureOn(m: Model, day: string): number | null {
  let v: number | null = null
  for (const p of m.expenditure) { if (p.day > day) break; v = p.E }
  return v
}

export function lastNDays(today: string, n: number): string[] {
  return dayRange(addDays(today, -(n - 1)), today)
}
